/**
 * Workload Tracking Prediction Service - Phase 4 Architecture
 *
 * Encapsulates workload prediction, capacity planning, team analytics, and reporting.
 * Extracted from WorkloadTrackingService to reduce single-responsibility violations.
 */

import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type {
  WorkloadSummary,
  WorkloadTracking,
  WorkloadTrend,
} from "@/types/time-management";
import { WorkloadTrackingCrudService } from "./workload-tracking-crud.service";
import { WorkloadTrackingAnalyticsService } from "./workload-tracking-analytics.service";

export class WorkloadTrackingPredictionService implements IService {
  public readonly name = "WorkloadTrackingPredictionService";
  private crudService: WorkloadTrackingCrudService;
  private analyticsService: WorkloadTrackingAnalyticsService;

  constructor(
    crudService: WorkloadTrackingCrudService,
    analyticsService: WorkloadTrackingAnalyticsService
  ) {
    this.crudService = crudService;
    this.analyticsService = analyticsService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ WorkloadTrackingPredictionService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ WorkloadTrackingPredictionService disposed");
  }

  async predictWorkload(
    userId: string,
    futureDays: number,
    considerSeasonality = true
  ): Promise<
    ServiceResponse<{
      predictions: WorkloadTracking[];
      confidence: "low" | "medium" | "high";
      factors: string[];
    }>
  > {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);

      const historicalResult = await this.crudService.getUserWorkload(userId, {
        start: startDate.toISOString().split("T")[0]!,
        end: endDate.toISOString().split("T")[0]!,
      });

      if (historicalResult.error || !historicalResult.data) {
        return createErrorResponse(
          historicalResult.error ?? "Failed to load historical workload"
        );
      }

      const historicalData = historicalResult.data;

      if (historicalData.length < 7) {
        return createErrorResponse("At least 7 days of historical data required for predictions");
      }

      const avgActualHours =
        historicalData.reduce((sum, entry) => sum + entry.actual_hours, 0) /
        historicalData.length;
      const avgTaskCount =
        historicalData.reduce((sum, entry) => sum + entry.task_count, 0) /
        historicalData.length;
      const avgProductivity =
        historicalData
          .filter((entry) => entry.productivity_score !== null)
          .reduce((sum, entry) => sum + entry.productivity_score!, 0) /
          historicalData.length || 7;

      const predictions: WorkloadTracking[] = [];
      const factors: string[] = ["Historical average", "Day of week patterns"];

      if (considerSeasonality) {
        factors.push("Seasonal trends");
      }

      let confidence: "low" | "medium" | "high" = "medium";

      if (historicalData.length < 14) {
        confidence = "low";
        factors.push("Limited historical data");
      } else if (historicalData.length > 60) {
        confidence = "high";
        factors.push("Extensive historical data");
      }

      for (let i = 1; i <= futureDays; i++) {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + i);

        const dayOfWeek = futureDate.getDay();
        const sameDayData = historicalData.filter(
          (entry) => new Date(entry.date).getDay() === dayOfWeek
        );

        const dayAvgHours =
          sameDayData.length > 0
            ? sameDayData.reduce((sum, entry) => sum + entry.actual_hours, 0) /
              sameDayData.length
            : avgActualHours;

        const dayAvgTasks =
          sameDayData.length > 0
            ? sameDayData.reduce((sum, entry) => sum + entry.task_count, 0) /
              sameDayData.length
            : avgTaskCount;

        predictions.push({
          id: `prediction-${i}`,
          user_id: userId,
          date: futureDate.toISOString().split("T")[0]!,
          planned_hours: Math.round(dayAvgHours * 10) / 10,
          actual_hours: Math.round(dayAvgHours * 10) / 10,
          task_count: Math.round(dayAvgTasks),
          completed_tasks: Math.round(dayAvgTasks * 0.85),
          focus_time_minutes: Math.round(dayAvgHours * 60 * 0.7),
          interruption_count: Math.round(dayAvgHours * 2),
          productivity_score: Math.round(avgProductivity * 10) / 10,
          notes: "AI-generated prediction",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }

      return createSuccessResponse({ predictions, confidence, factors });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async assessWorkloadCapacity(
    _userId: string,
    plannedTasks: {
      estimated_duration: number;
      priority: "low" | "medium" | "high";
      due_date: string;
    }[],
    constraints: {
      maxDailyHours?: number;
      maxWeeklyHours?: number;
      workingDays?: number[];
      bufferPercentage?: number;
    }
  ): Promise<
    ServiceResponse<{
      canAccommodate: boolean;
      workloadDistribution: Record<string, number>;
      overloadDates: string[];
      recommendations: string[];
      alternativeSchedule?: Record<string, number>;
    }>
  > {
    try {
      const maxDailyHours = constraints.maxDailyHours ?? 8;
      const bufferPercentage = constraints.bufferPercentage ?? 0.2;

      const tasksByDate: Record<string, number> = {};
      plannedTasks.forEach((task) => {
        if (!tasksByDate[task.due_date]) {
          tasksByDate[task.due_date] = 0;
        }
        tasksByDate[task.due_date] += task.estimated_duration / 60;
      });

      const workloadDistribution: Record<string, number> = {};
      const overloadDates: string[] = [];
      const recommendations: string[] = [];

      Object.keys(tasksByDate).forEach((date) => {
        const hoursNeeded = tasksByDate[date] ?? 0;
        const effectiveCapacity = maxDailyHours * (1 - bufferPercentage);

        workloadDistribution[date] = hoursNeeded;

        if (hoursNeeded > effectiveCapacity) {
          overloadDates.push(date);
        }
      });

      const canAccommodate = overloadDates.length === 0;

      if (!canAccommodate) {
        recommendations.push("Consider redistributing tasks across multiple days");
        recommendations.push("Review task priorities and deadlines");
      }

      if (overloadDates.length > 0) {
        recommendations.push(`${overloadDates.length} dates exceed capacity`);
      }

      return createSuccessResponse({
        canAccommodate,
        workloadDistribution,
        overloadDates,
        recommendations,
        alternativeSchedule: canAccommodate ? undefined : workloadDistribution,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async optimizeWorkloadDistribution(
    _userId: string,
    tasks: {
      id: string;
      estimated_duration: number;
      priority: "low" | "medium" | "high";
      due_date: string;
      flexibility_hours?: number;
    }[],
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      optimizedSchedule: Record<string, string[]>;
      workloadBalance: number;
      utilizationRate: number;
      improvements: string[];
    }>
  > {
    try {
      const optimizedSchedule: Record<string, string[]> = {};
      const improvements: string[] = [];

      const sortedTasks = [...tasks].sort((a, b) => {
        const priorityWeight = { high: 3, medium: 2, low: 1 };
        const priorityDiff =
          priorityWeight[b.priority] - priorityWeight[a.priority];
        if (priorityDiff !== 0) {return priorityDiff;}
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      const dateKeys = this.generateDateRange(dateRange.start, dateRange.end);
      let currentDateIndex = 0;

      sortedTasks.forEach((task) => {
        const targetDate = dateKeys[currentDateIndex % dateKeys.length] ?? dateRange.start;
        (optimizedSchedule[targetDate] ??= []).push(task.id);
        currentDateIndex++;
      });

      const totalHours = tasks.reduce(
        (sum, task) => sum + task.estimated_duration / 60,
        0
      );
      const workingDaysCount = dateKeys.length;
      const averageHoursPerDay = totalHours / (workingDaysCount || 1);
      const workloadBalance =
        averageHoursPerDay <= 8 ? 1.0 : 8 / averageHoursPerDay;
      const utilizationRate = Math.min(averageHoursPerDay / 8, 1.0);

      if (workloadBalance < 0.8) {
        improvements.push("Consider extending deadline or reducing scope");
      }
      if (utilizationRate > 0.9) {
        improvements.push("High utilization - monitor for potential burnout");
      }

      return createSuccessResponse({
        optimizedSchedule,
        workloadBalance,
        utilizationRate,
        improvements,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async getTeamWorkloadOverview(
    userIds: string[],
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      teamSummary: WorkloadSummary;
      individualSummaries: Record<string, WorkloadSummary>;
      teamBalance: number;
      redistributionSuggestions: {
        fromUser: string;
        toUser: string;
        estimatedHours: number;
        reasoning: string;
      }[];
    }>
  > {
    try {
      const individualSummaries: Record<string, WorkloadSummary> = {};
      let totalPlannedHours = 0;
      let totalActualHours = 0;
      let totalTasks = 0;
      let totalCompleted = 0;

      for (const userId of userIds) {
        const summaryResult = await this.analyticsService.getWorkloadSummary(
          userId,
          dateRange
        );
        if (summaryResult.data) {
          individualSummaries[userId] = summaryResult.data;
          totalPlannedHours += summaryResult.data.total_planned_hours;
          totalActualHours += summaryResult.data.total_actual_hours;
          totalTasks += summaryResult.data.total_tasks;
          totalCompleted += summaryResult.data.total_completed;
        }
      }

      const teamSummary: WorkloadSummary = {
        total_planned_hours: totalPlannedHours,
        total_actual_hours: totalActualHours,
        total_tasks: totalTasks,
        total_completed: totalCompleted,
        completion_rate:
          totalTasks > 0 ? (totalCompleted / totalTasks) * 100 : 0,
        average_productivity_score:
          Object.values(individualSummaries).reduce(
            (sum, summary) => sum + summary.average_productivity_score,
            0
          ) / userIds.length,
        trends: [],
      };

      const utilizationRates = Object.values(individualSummaries).map(
        (summary) =>
          summary.total_actual_hours / summary.total_planned_hours || 0
      );
      const avgUtilization =
        utilizationRates.reduce((sum, rate) => sum + rate, 0) /
        utilizationRates.length;
      const utilVariance =
        utilizationRates.reduce(
          (sum, rate) => sum + Math.pow(rate - avgUtilization, 2),
          0
        ) / utilizationRates.length;
      const teamBalance = Math.max(0, 1 - utilVariance);

      const redistributionSuggestions: {
        fromUser: string;
        toUser: string;
        estimatedHours: number;
        reasoning: string;
      }[] = [];

      const overloadedUsers = Object.entries(individualSummaries).filter(
        ([_, summary]) =>
          summary.total_actual_hours > summary.total_planned_hours * 1.2
      );
      const underloadedUsers = Object.entries(individualSummaries).filter(
        ([_, summary]) =>
          summary.total_actual_hours < summary.total_planned_hours * 0.8
      );

      overloadedUsers
        .slice(0, 2)
        .forEach(([overloadedUserId, overloadedSummary]) => {
          underloadedUsers
            .slice(0, 1)
            .forEach(([underloadedUserId, underloadedSummary]) => {
              const excessHours =
                overloadedSummary.total_actual_hours -
                overloadedSummary.total_planned_hours;
              const availableCapacity =
                underloadedSummary.total_planned_hours -
                underloadedSummary.total_actual_hours;
              const redistributeHours =
                Math.min(excessHours, availableCapacity) * 0.5;

              if (redistributeHours > 1) {
                redistributionSuggestions.push({
                  fromUser: overloadedUserId,
                  toUser: underloadedUserId,
                  estimatedHours: Math.round(redistributeHours * 10) / 10,
                  reasoning: "Balance workload distribution",
                });
              }
            });
        });

      return createSuccessResponse({
        teamSummary,
        individualSummaries,
        teamBalance,
        redistributionSuggestions,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async generateWorkloadReport(
    userId: string,
    dateRange: { start: string; end: string },
    reportType: "summary" | "detailed" | "insights"
  ): Promise<
    ServiceResponse<{
      reportData:
        | WorkloadSummary
        | WorkloadTracking[]
        | Record<string, unknown>;
      insights: string[];
      recommendations: string[];
      charts: {
        type: "line" | "bar" | "pie";
        data: Record<string, unknown>;
        title: string;
      }[];
    }>
  > {
    try {
      let reportData:
        | WorkloadSummary
        | WorkloadTracking[]
        | Record<string, unknown>;
      const insights: string[] = [];
      const recommendations: string[] = [];
      const charts: {
        type: "line" | "bar" | "pie";
        data: Record<string, unknown>;
        title: string;
      }[] = [];

      switch (reportType) {
        case "summary": {
          const summaryResult = await this.analyticsService.getWorkloadSummary(
            userId,
            dateRange
          );
          if (summaryResult.error) {
            return createErrorResponse(summaryResult.error ?? "Summary failed");
          }
          reportData = summaryResult.data!;

          insights.push(
            `Completion rate: ${Math.round(summaryResult.data!.completion_rate)}%`
          );
          insights.push(
            `Total actual hours: ${summaryResult.data!.total_actual_hours}`
          );

          if (summaryResult.data!.completion_rate < 80) {
            recommendations.push("Focus on improving task completion rate");
          }
          break;
        }

        case "detailed": {
          const detailedResult = await this.crudService.getUserWorkload(userId, dateRange);
          if (detailedResult.error) {
            return createErrorResponse(detailedResult.error ?? "Detailed fetch failed");
          }
          reportData = detailedResult.data!;

          insights.push(`${detailedResult.data!.length} days of data analyzed`);
          break;
        }

        case "insights": {
          const patternsResult = await this.analyticsService.identifyWorkloadPatterns(
            userId,
            dateRange
          );
          if (patternsResult.error) {
            return createErrorResponse(patternsResult.error ?? "Patterns failed");
          }
          reportData = patternsResult.data!;

          insights.push(
            `${patternsResult.data!.anomalies.length} anomalies detected`
          );
          insights.push("Weekly patterns analyzed");
          break;
        }

        default:
          reportData = {};
      }

      charts.push({
        type: "line",
        data: { placeholder: "Chart data would go here" },
        title: "Workload Trend",
      });

      return createSuccessResponse({
        reportData,
        insights,
        recommendations,
        charts,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async autoUpdateFromTasks(
    _userId: string,
    _date: string
  ): Promise<ServiceResponse<WorkloadTracking>> {
    return createErrorResponse("Auto-update from tasks not yet implemented");
  }

  async syncWithTimeBlocks(
    _userId: string,
    _date: string,
    _timeBlockIds: string[]
  ): Promise<ServiceResponse<WorkloadTracking>> {
    return createErrorResponse("Sync with time blocks not yet implemented");
  }

  private generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]!);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }
}
